"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DrawActionScene = void 0;
const pixi_js_1 = require("pixi.js");
const Global_1 = require("../Global");
const blocks_1 = require("../blocks");
const interfaces_1 = require("../../../shared/interfaces");
const DrawManager_1 = require("../managers/DrawManager");
const i18next_1 = __importDefault(require("i18next"));
const arrow_right_svg_1 = __importDefault(require("../../assets/svg/arrow_right.svg"));
const gsap_1 = require("gsap");
const palette_svg_1 = __importDefault(require("../../assets/svg/palette.svg"));
const lodash_1 = __importDefault(require("lodash"));
const AtelierSelector_1 = require("../blocks/AtelierSelector");
const colors_1 = require("../config/colors");
const settings_1 = require("../config/settings");
const ActionScene_1 = require("./ActionScene");
const NameBanner_1 = require("../blocks/NameBanner");
const levels_1 = require("../../../shared/levels");
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
class DrawActionScene extends ActionScene_1.ActionScene {
    constructor(app, sceneManager, gameManager) {
        var _a, _b;
        super(app, sceneManager, gameManager);
        this.areaMask = new pixi_js_1.Graphics();
        this.atelieropen = false;
        this.start = null;
        this.startX = null;
        this.atelierDragStart = (evt) => {
            this.start = evt.getLocalPosition(this.app.stage);
            this.startX = this.atelier.x;
            this.diff = 0;
        };
        this.atelierDragMove = (evt) => {
            var _a, _b;
            const now = evt.getLocalPosition(this.app.stage);
            const xdiff = now.x - ((_a = this.start) === null || _a === void 0 ? void 0 : _a.x);
            if (this.diff) {
                const diff = now.x - this.diff;
                this.atelier.x = Math.max(now.x + diff, this.startX);
            }
            else if (this.start) {
                const ydiff = now.y - ((_b = this.start) === null || _b === void 0 ? void 0 : _b.y);
                if (!this.diff && xdiff > (0, Global_1.px)(20) && Math.abs(ydiff) < (0, Global_1.px)(2)) {
                    this.diff = now.x;
                    this.atelier.enabled = false;
                    this.atelier.x += xdiff;
                }
            }
        };
        this.atelierDragStop = (evt) => {
            this.start = null;
            if (this.diff) {
                if (this.atelier.x > (0, Global_1.right)(this.app.screen.width)) {
                    this.atelierhide(true).then();
                }
                else {
                    this.ateliershow(true).then();
                    this.atelier.enabled = true;
                }
                this.diff = 0;
            }
        };
        this.atelierButton = this.setupAtelierButton();
        this.guess = this.makeGuess();
        this.updateGuess();
        this.myManager = new DrawManager_1.DrawManager(this.myCanvas, this.gameManager);
        this.subs.add(this.myManager.updated$.subscribe({
            next: (encoded) => __awaiter(this, void 0, void 0, function* () {
                this.gameManager.submitDraw(encoded);
                yield this.atelierhide();
            })
        }));
        this.subs.add(this.myManager.erased$.subscribe({
            next: () => {
                this.updateGuess();
            }
        }));
        this.subs.add(this.gameManager.playerManager.playerData$.subscribe((data) => {
            const color = colors_1.colors[data[interfaces_1.Store.Color]];
            this.myCanvas.stroke = color || settings_1.Settings.drawColor;
        }));
        this.areaMask = this.addChild(this.setupAreaMask());
        this.atelier = this.setupAtelier();
        if (((_a = this.round) === null || _a === void 0 ? void 0 : _a.type) !== interfaces_1.GameType.Practice && ((_b = this.round) === null || _b === void 0 ? void 0 : _b.type) !== interfaces_1.GameType.Tutorial) {
            const data = this.gameManager.playerManager.data;
            this.myNametag = this.makeNametag(data.user, data.lvl ? data.lvl - 1 : null);
        }
        let breaker = 0;
        let timer = null;
        this.subs.add(this.gameManager.roomStream.submit$.subscribe({
            next: (item) => {
                // A bit of debouncing, maybe we should do this on the backend instead
                if (item.pkey === this.gameManager.playerManager.pkey) {
                    clearTimeout(timer);
                    timer = setTimeout(() => {
                        this.updateGuess(item.labels);
                        breaker = 0;
                    }, breaker++ > 50 ? 0 : 50);
                }
                else {
                    // opponent update, no need to do anything with that
                }
            }
        }));
        if ((0, Global_1.isDebug)()) {
            const texture = pixi_js_1.Texture.from(this.myManager.scaled);
            const sprite = new pixi_js_1.Sprite(texture);
            this.addChild(sprite);
            this.subs.add(this.myCanvas.strokes$.subscribe({
                next: () => {
                    texture.update();
                }
            }));
        }
    }
    setupAtelier() {
        if (!this.atelier) {
            this.atelier = new AtelierSelector_1.AtelierSelector(this.app, this.gameManager, this.atelierSize);
            this.atelier.zIndex = 4;
            this.atelier.mask = this.areaMask;
            // Make back button
            const back = this.makeButton(arrow_right_svg_1.default, 0.01);
            this.sigs.push(back.onPress.connect(() => __awaiter(this, void 0, void 0, function* () {
                AssetManager_1.default.tickSound();
                yield this.atelierhide(true);
            })));
            back.name = 'back';
            this.atelier.addChild(back);
            this.addChild(this.atelier);
            this.atelier.eventMode = 'static';
            this.atelier.on('pointerdown', this.atelierDragStart);
            this.atelier.on('globalpointermove', this.atelierDragMove);
            this.atelier.on('pointerup', this.atelierDragStop);
            this.atelier.on('pointerupoutside', this.atelierDragStop);
        }
        else {
            this.atelier.size = this.atelierSize;
        }
        this.atelier.position = this.atelierpos;
        // this.atelier.position = this.center;
        const back = this.atelier.getChildByName('back');
        back === null || back === void 0 ? void 0 : back.scale.set((0, Global_1.px)(0.18));
        this.atelier.visible = this.gameManager.playerManager.ownsAnything();
        this.atelieropen = false;
        return this.atelier;
    }
    get atelierpos() {
        var _a;
        return new pixi_js_1.Point((0, Global_1.right)(this.app.screen.width, -this.atelier.width / (0, Global_1.px)(2)), ((_a = this.myCanvas) === null || _a === void 0 ? void 0 : _a.y)
            + (0, Global_1.px)(this.atelier.size / 2) - (0, Global_1.px)(47));
    }
    get atelierSize() {
        return 180;
    }
    ateliershow(force = false) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.atelieropen || force) {
                this.atelieropen = true;
                return yield this.promisedTimeline(gsap_1.gsap.timeline()
                    .to(this.atelier, {
                    pixi: {
                        positionX: this.atelierButtonPos.x + (0, Global_1.px)(20)
                    },
                    duration: 0.2,
                    ease: 'power2.out'
                })
                    .to(this.atelierButton, {
                    pixi: {
                        positionX: this.atelierpos.x
                    },
                    duration: 0.2
                }, '<'));
            }
        });
    }
    atelierhide(force = false) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.atelieropen || force) {
                this.atelieropen = false;
                return yield this.promisedTimeline(gsap_1.gsap.timeline()
                    .to(this.atelier, {
                    pixi: {
                        positionX: this.atelierpos.x
                    },
                    duration: 0.2,
                    ease: 'power2.in'
                })
                    .to(this.atelierButton, {
                    pixi: {
                        positionX: this.atelierButtonPos.x
                    },
                    duration: 0.2
                }, '<'));
            }
        });
    }
    setupAtelierButton() {
        if (!this.atelierButton) {
            this.atelierButton = this.makeButton(palette_svg_1.default, 0.03);
            this.atelierButton.mask = this.areaMask;
            this.sigs.push(this.atelierButton.onPress.connect(() => __awaiter(this, void 0, void 0, function* () {
                AssetManager_1.default.wooshSound();
                yield this.ateliershow();
                this.atelier.enabled = true;
            })));
            this.addChild(this.atelierButton);
        }
        if (this.isLandscapeBox) {
            this.atelierButton.scale.set((0, Global_1.px)(0.2));
        }
        else {
            this.atelierButton.scale.set((0, Global_1.px)(0.3));
        }
        this.atelierButton.position = this.atelierButtonPos;
        this.atelierButton.visible = this.gameManager.playerManager.ownsAnything();
        return this.atelierButton;
    }
    setupAreaMask() {
        return this.areaMask.clear()
            .beginFill(settings_1.Settings.whiteColor)
            .drawRect(0, 0, (0, Global_1.px)(Math.min(this.app.screen.width, settings_1.Settings.maxWidth)), this.myCanvas.y + this.myCanvas.height)
            .endFill();
    }
    get atelierButtonPos() {
        var _a, _b, _c, _d, _e, _f;
        const rpad = 10;
        const bpad = 18;
        if (this.isLandscapeBox) {
            return {
                x: ((_a = this.myCanvas) === null || _a === void 0 ? void 0 : _a.x) - ((_b = this.myCanvas) === null || _b === void 0 ? void 0 : _b.pivot.x) + ((_c = this.myCanvas) === null || _c === void 0 ? void 0 : _c.width) + this.atelierButton.width / 2,
                y: ((_d = this.myCanvas) === null || _d === void 0 ? void 0 : _d.y) + (0, Global_1.px)(bpad / 2)
            };
        }
        else {
            return {
                x: this.center.x + ((_e = this.myCanvas) === null || _e === void 0 ? void 0 : _e.width) / 2 - this.atelierButton.width / 2 - (0, Global_1.px)(rpad),
                y: ((_f = this.myCanvas) === null || _f === void 0 ? void 0 : _f.y) - (0, Global_1.px)(bpad)
            };
        }
    }
    makeOpponent(pkey) {
        const opponent = new blocks_1.DrawOpponent(this.app, this.gameManager, pkey);
        opponent.scale.set(this.overSmallSize ? 1 : 0.88);
        opponent.zIndex = 4;
        return this.addChild(opponent);
    }
    makeGuess() {
        const style = blocks_1.Block.textStyle(22);
        style.fill = settings_1.Settings.blackColor;
        style.align = 'center';
        style.wordWrap = true;
        style.wordWrapWidth = (0, Global_1.px)(120);
        const guess = new pixi_js_1.Text(null, style);
        guess.anchor.set(0.5, 1.0);
        return this.addChild(guess);
    }
    finish(winner = null) {
        const _super = Object.create(null, {
            finish: { get: () => super.finish }
        });
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // If we are in levels we should update our player, ask for an update
            if (((_a = this.round) === null || _a === void 0 ? void 0 : _a.type) === interfaces_1.GameType.Levels) {
                yield this.gameManager.playerManager.refresh();
            }
            yield _super.finish.call(this, winner);
        });
    }
    makeNametag(name, lvl = null, width = 120, height = 42) {
        const container = new NameBanner_1.NameBanner(this.app, name, lvl !== null ? levels_1.levels[lvl] : null, width);
        container._color = settings_1.Settings.lightLightGrayColor;
        container.pivot.set((0, Global_1.px)(container.w / 2), (0, Global_1.px)(container.h / 2));
        container.zIndex = 3;
        return this.addChild(container);
    }
    updateGuess(labels = []) {
        var _a;
        const ss = (0, Global_1.i18nextss)();
        if (labels === null || labels === void 0 ? void 0 : labels.length) {
            const label = labels.map((l) => {
                return (0, Global_1.category)(l, ss !== settings_1.SentenceStructure.OAV);
            }).join(' ' + i18next_1.default.t('or') + ' ');
            this.guess.style.fontSize = (0, Global_1.fontSize)(20);
            this.guess.style.fill = settings_1.Settings.darkGrayColor;
            const d = i18next_1.default.t('looks');
            if (ss === settings_1.SentenceStructure.OAV || ss === settings_1.SentenceStructure.AOV) {
                this.guess.text = label + ' ' + d;
            }
            else {
                this.guess.text = d + ' ' + label;
            }
        }
        else {
            const label = (_a = this.round) === null || _a === void 0 ? void 0 : _a.label;
            this.guess.style.fontSize = (0, Global_1.fontSize)(22);
            this.guess.style.fill = settings_1.Settings.blackColor;
            const d = i18next_1.default.t('draw');
            const l = (0, Global_1.category)(label);
            if (ss === settings_1.SentenceStructure.OAV || ss === settings_1.SentenceStructure.AOV) {
                this.guess.text = (0, Global_1.category)(label) + ' ' + d;
            }
            else {
                this.guess.text = d + ' ' + (0, Global_1.category)(label);
            }
        }
    }
    addOpponent(player) {
        const _super = Object.create(null, {
            addOpponent: { get: () => super.addOpponent }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const opponent = yield _super.addOpponent.call(this, player);
            if (!opponent.visible) {
                opponent.position.y = this.app.screen.height;
                opponent.rotation = 0;
                opponent.visible = true;
                try {
                    yield Promise.all([this.promisedTimeline(gsap_1.gsap.timeline()
                            .to(opponent, {
                            pixi: {
                                positionY: this.targetY(opponent)
                            },
                            ease: 'power3.out',
                            duration: 0.5
                        })), opponent.canvas.outOverlay()]);
                }
                catch (e) { }
            }
            this.layout();
            return opponent;
        });
    }
    dropOpponent(player) {
        const _super = Object.create(null, {
            dropOpponent: { get: () => super.dropOpponent }
        });
        return __awaiter(this, void 0, void 0, function* () {
            const opponent = yield _super.dropOpponent.call(this, player);
            if (opponent) {
                opponent.visible = true;
                try {
                    yield opponent.canvas.inOverlay(settings_1.Settings.whiteColor);
                    yield this.promisedTimeline(gsap_1.gsap.timeline()
                        .to(opponent, {
                        yoyo: true,
                        keyframes: [
                            { pixi: { rotation: 16 } },
                            { pixi: { rotation: 8 } },
                            { pixi: { rotation: 11 } }
                        ],
                        duration: 0.5
                    })
                        .to(opponent, {
                        pixi: {
                            positionY: this.app.screen.height
                        },
                        ease: 'power3.in',
                        duration: 0.5
                    }));
                }
                catch (e) { }
                opponent.visible = false;
            }
            return opponent;
        });
    }
    destroy(_options) {
        var _a, _b, _c, _d, _e;
        (_a = this.myManager) === null || _a === void 0 ? void 0 : _a.destroy();
        clearInterval(this.roundtimer);
        (_b = this.atelier) === null || _b === void 0 ? void 0 : _b.off('pointerdown', this.atelierDragStart);
        (_c = this.atelier) === null || _c === void 0 ? void 0 : _c.off('pointermove', this.atelierDragMove);
        (_d = this.atelier) === null || _d === void 0 ? void 0 : _d.off('pointerup', this.atelierDragStop);
        (_e = this.atelier) === null || _e === void 0 ? void 0 : _e.off('pointerupoutside', this.atelierDragStop);
        super.destroy(_options);
    }
    targetY(op) {
        return this.app.screen.height - op.height - (0, Global_1.px)(10);
    }
    targetX(op) {
        return this.app.screen.width - op.width - (0, Global_1.px)(8);
    }
    layout() {
        var _a;
        super.layout();
        if (this.opponents) {
            let i = 0;
            for (let op of lodash_1.default.values(this.opponents)) {
                if (this.isLandscapeBox) {
                    if (i++ % 2 === 0) {
                        op.position.set((0, Global_1.px)(8), this.targetY(op));
                    }
                    else {
                        op.position.set(this.targetX(op), this.targetY(op));
                    }
                }
                else {
                    if (i++ % 2 === 0) {
                        op.position.set(this.center.x - this.myCanvas.width / 2 + (0, Global_1.px)(5), this.targetY(op));
                    }
                    else {
                        op.position.set(this.center.x + this.myCanvas.width / 2 - op.width - (0, Global_1.px)(5), this.targetY(op));
                    }
                }
                op.scale.set(this.overSmallSize ? 1 : 0.88);
            }
        }
        (_a = this.myNametag) === null || _a === void 0 ? void 0 : _a.position.set(this.center.x, this.myCanvas.y + (0, Global_1.px)(0));
        if (this.guess) {
            this.guess.position.set(this.center.x, this.app.screen.height - (0, Global_1.px)(this.isLandscapeBox ? 8 : 20));
            this.guess.style.wordWrapWidth = (0, Global_1.px)(this.isLandscapeBox ? 230 : 120);
        }
        this.setupAtelierButton();
        this.setupAtelier();
        this.setupAreaMask().position.set((0, Global_1.left)(this.app.screen.width), 0);
    }
}
exports.DrawActionScene = DrawActionScene;
